import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
} from '@mui/material';
import {
  PlayArrow as TestIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  CheckCircle as CheckIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudQueue as ProviderIcon,
  Memory as ModelIcon,
  ViewInAr as VectorSizeIcon,
  InfoOutlined as DescriptionIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { seededConfigName, seededConfigDescription } from '../../utils/config/seededConfigText';
import type { EmbeddingConfig } from '../../api/ApiClient';
import { ConfigDetailRow } from '../ui/ConfigDetailRow';

export interface EmbeddingConfigCardProps {
  config: EmbeddingConfig;
  onTest: (config: EmbeddingConfig) => void;
  onResetToEnv: () => void;
  onSetToActivate: (config: EmbeddingConfig) => void;
  onEdit: (config: EmbeddingConfig) => void;
  onDelete: (configId: string) => void;
}

export function EmbeddingConfigCard({
  config,
  onTest,
  onResetToEnv,
  onSetToActivate,
  onEdit,
  onDelete,
}: EmbeddingConfigCardProps) {
  const { t } = useTranslation();

  const handleActivateClick = () => {
    if (!config.is_active) {
      if (!config.id) {
        // Environment default — use reset to env
        onResetToEnv();
      } else {
        // Regular config — open activate dialog
        onSetToActivate(config);
      }
    }
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent sx={{ p: { xs: 2, md: 3 }, '&:last-child': { pb: { xs: 2, md: 3 } } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'flex-start' },
            gap: { xs: 2, md: 3 },
          }}
        >
          {/* LEFT: title + badges */}
          <Box sx={{ width: { xs: '100%', md: 200 }, flexShrink: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, lineHeight: 1.3 }}>
              {seededConfigName(config.display_name, t) || `${config.provider} Configuration`}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {/* Standard badge: shown when !config.id (env default) */}
              {!config.id && (
                <Chip
                  label={t('embedding.currentConfig.default')}
                  color="primary"
                  size="small"
                  icon={<StarIcon />}
                />
              )}
              {/* Active badge */}
              {config.is_active && (
                <Chip
                  label={t('embedding.currentConfig.active')}
                  color="success"
                  size="small"
                  icon={<CheckIcon />}
                />
              )}
            </Box>
          </Box>

          {/* Vertical divider — md+ only */}
          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: 'none', md: 'block' } }}
          />

          {/* MIDDLE: detail rows */}
          <Box sx={{ flexGrow: 1 }}>
            <Stack spacing={1.25}>
              <ConfigDetailRow
                icon={<ProviderIcon fontSize="small" />}
                label={t('embedding.currentConfig.provider')}
                value={config.provider}
              />
              <ConfigDetailRow
                icon={<ModelIcon fontSize="small" />}
                label={t('embedding.currentConfig.model')}
                value={config.model}
              />
              <ConfigDetailRow
                icon={<VectorSizeIcon fontSize="small" />}
                label={t('embedding.currentConfig.vectorSize')}
                value={config.vector_size}
              />
              {config.description && (
                <ConfigDetailRow
                  icon={<DescriptionIcon fontSize="small" />}
                  label={t('embedding.currentConfig.description')}
                  value={seededConfigDescription(config.description, t)}
                  alignItems="flex-start"
                />
              )}
            </Stack>
          </Box>

          {/* RIGHT: action pill */}
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: 'action.hover',
                borderRadius: 2,
                p: 0.5,
              }}
            >
              {/* Test */}
              <IconButton
                size="small"
                onClick={() => onTest(config)}
                title={t('embedding.actions.testConfiguration')}
                aria-label={t('embedding.actions.testConfiguration')}
              >
                <TestIcon fontSize="small" />
              </IconButton>

              {/* Activate / star */}
              <IconButton
                size="small"
                onClick={handleActivateClick}
                title={config.is_active ? t('embedding.dialog.currentlyActive') : t('embedding.dialog.setAsActive')}
                aria-label={config.is_active ? t('embedding.dialog.currentlyActive') : t('embedding.dialog.setAsActive')}
                color={config.is_active ? 'warning' : 'default'}
                disabled={config.is_active}
              >
                {config.is_active ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
              </IconButton>

              {/* Edit — only for DB-persisted configs */}
              {config.id && (
                <IconButton
                  size="small"
                  onClick={() => onEdit(config)}
                  title={t('embedding.actions.editConfiguration')}
                  aria-label={t('embedding.actions.editConfiguration')}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}

              {/* Delete — only for DB-persisted configs */}
              {config.id && (
                <IconButton
                  size="small"
                  onClick={() => onDelete(config.id!)}
                  title={t('embedding.actions.deleteConfiguration')}
                  aria-label={t('embedding.actions.deleteConfiguration')}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
